import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChapterHeading, Reveal } from "./Reveal";
import { IMAGES } from "../config/site";

const BLOCKS = [
  {
    key: "create",
    title: "Create",
    body: "Quick grills with a pizza oven and wok station, a catering kitchen, and a music room.",
  },
  {
    key: "connect",
    title: "Connect",
    body: "Porte cochère, lobby lounges, the library, mauka and makai terraces and lānai, BBQ cabanas, a theatre, a dog park, guest suites, and a 24-hour concierge.",
  },
  {
    key: "move",
    title: "Move",
    body: "Heated saltwater pool, fitness center, pickleball court, mauka and tranquility lawns, tropical gardens, and a pebble reflexology path.",
  },
  {
    key: "recover",
    title: "Recover",
    body: "Aescape robotic massage, the Ammortal wellness chamber, TOTO NEOREST zero-gravity flotation tubs, sauna and steam rooms, hot and cold plunges, a hot tub, and a massage room.",
  },
];

const TECH = [
  {
    key: "aescape",
    name: "Aescape",
    body: "The world's first robotic massage system — customizable, self-guided, fully clothed sessions that map your body and adjust in real time, with golf, surf, and paddle programs.",
  },
  {
    key: "ammortal",
    name: "Ammortal Chamber",
    body: "A 30-minute restorative session stacking red and near-infrared light, PEMF, vibroacoustics, molecular hydrogen, oxygen, and guided meditation — based on NASA zero-gravity positioning research.",
  },
  {
    key: "toto",
    name: "TOTO NEOREST Flotation Tub",
    body: "A near-weightless bathing experience — ZERO DIMENSION positioning, Hydrohands massaging jets, a cascading shoulder warmer, and mood lighting.",
  },
];

const WellnessClub = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section className="py-24 md:py-32 bg-sand" data-testid="wellness-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ChapterHeading
          number="04"
          overline="The Wellness Club"
          title="&ldquo;Hawai&rsquo;i&rsquo;s first wellness-integrated boutique residence.&rdquo;"
        />

        <Reveal delay={0.1}>
          <p className="mt-8 md:mt-10 md:ml-[25%] max-w-2xl text-base text-stone2 leading-relaxed" data-testid="wellness-subline">
            That&rsquo;s how the developer describes Kali&rsquo;u. The planned amenities below come from the developer&rsquo;s materials &mdash; grouped here for easy reading.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-14 md:mt-20 items-start">
          <Reveal className="lg:col-span-5">
            <div ref={imgRef} className="relative overflow-hidden aspect-[3/4]" data-testid="wellness-image-frame">
              <motion.img
                src={IMAGES.wellnessSpa}
                alt="Abstract close-up of calm water surface at dusk — placeholder imagery"
                className="absolute inset-0 w-full h-[120%] object-cover"
                style={{ y }}
                data-testid="wellness-image"
              />
            </div>
            <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-stone2">Atmospheric placeholder — not a developer rendering</p>
          </Reveal>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line" data-testid="wellness-blocks">
            {BLOCKS.map((b, i) => (
              <Reveal key={b.key} delay={i * 0.1} className="bg-ivory">
                <div className="p-8 md:p-10 h-full hover:bg-sand transition-colors duration-700" data-testid={`wellness-block-${b.key}`}>
                  <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-teal">{b.title}</p>
                  <p className="text-sm text-stone2 leading-relaxed mt-4">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-16 md:mt-20 text-[10px] tracking-[0.25em] uppercase font-semibold text-teal" data-testid="wellness-tech-overline">The recovery technology</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line mt-6" data-testid="wellness-tech-blocks">
          {TECH.map((t, i) => (
            <Reveal key={t.key} delay={i * 0.1} className="bg-ivory">
              <div className="p-8 md:p-10 h-full hover:bg-ivory/80 transition-colors duration-700" data-testid={`wellness-tech-${t.key}`}>
                <p className="font-serif text-xl md:text-2xl font-light text-charcoal">{t.name}</p>
                <p className="text-sm text-stone2 leading-relaxed mt-4">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 text-xs text-stone2 leading-relaxed italic max-w-3xl" data-testid="wellness-caveat">
            Amenities are proposed by the developer and subject to change. Access may require membership or fees — confirm in the developer&rsquo;s official offering documents.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default WellnessClub;
